import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Activity,
  Stethoscope,
  Shield,
  User,
} from "lucide-react";
import InteractiveToothChart from "../../ToothChartPortable/InteractiveToothChart";
import { generateMockTeeth } from "../../ToothChartPortable/mockData";
import { Badge } from "../../ui/badge";

/* ── Phase config ─────────────────────────────────────── */

const phases = [
  {
    key: "odontogram",
    label: "Visual Odontogram",
    desc: "Full 32-tooth map with real-time condition tracking",
    icon: Activity,
    color: "blue",
    highlightTeeth: null,
    selectedTooth: null,
    stat: "32 teeth · all conditions mapped",
  },
  {
    key: "treatments",
    label: "Treatment Tracking",
    desc: "Planned & completed treatments across every tooth",
    icon: Stethoscope,
    color: "emerald",
    highlightTeeth: new Set([3, 4, 5, 13, 14, 19, 20, 28, 29, 30]),
    selectedTooth: 14,
    stat: "3 crowns · 1 implant · 1 root canal",
  },
  {
    key: "perio",
    label: "Perio Charting",
    desc: "Pocket depths, bleeding points, recession & mobility",
    icon: Shield,
    color: "amber",
    highlightTeeth: new Set([
      17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32,
    ]),
    selectedTooth: 19,
    stat: "Lower arch · pocket depths charted",
  },
];

const PHASE_DURATION = 4500;
const NUM_PHASES = phases.length;

const colorClasses = {
  blue: {
    bg: "bg-blue-100",
    text: "text-blue-700",
    border: "border-blue-200",
    dot: "bg-blue-500",
    ring: "ring-blue-400/40",
    barBg: "bg-blue-500",
  },
  emerald: {
    bg: "bg-emerald-100",
    text: "text-emerald-700",
    border: "border-emerald-200",
    dot: "bg-emerald-500",
    ring: "ring-emerald-400/40",
    barBg: "bg-emerald-500",
  },
  amber: {
    bg: "bg-amber-100",
    text: "text-amber-700",
    border: "border-amber-200",
    dot: "bg-amber-500",
    ring: "ring-amber-400/40",
    barBg: "bg-amber-500",
  },
};

/* ── Enhanced tooth data for odontogram overlays ──────── */

function buildEnhancedTeeth() {
  const map = {};

  // Completed crowns
  [3, 14, 30].forEach((n) => {
    map[n] = {
      toothNumber: n,
      baseStatus: "permanent",
      procedures: {
        completed: [{ type: "crown", color: "#388E3C" }],
        planned: [],
      },
      surfaces: { completed: [], planned: [] },
      caries: [],
    };
  });

  // Implant with abutment
  map[4] = {
    toothNumber: 4,
    baseStatus: "permanent",
    procedures: {
      completed: [
        { type: "implant", color: "#388E3C" },
        { type: "abutment", color: "#388E3C" },
      ],
      planned: [],
    },
    surfaces: { completed: [], planned: [] },
    caries: [],
  };

  // Root canal
  map[19] = {
    toothNumber: 19,
    baseStatus: "permanent",
    procedures: {
      completed: [{ type: "roots", color: "#388E3C" }],
      planned: [],
    },
    surfaces: { completed: [], planned: [] },
    caries: ["O"],
  };

  // Planned crown
  map[20] = {
    toothNumber: 20,
    baseStatus: "permanent",
    procedures: {
      completed: [],
      planned: [{ type: "crown", color: "#448AFF" }],
    },
    surfaces: {
      completed: [],
      planned: [
        { surface: "M", color: "#448AFF" },
        { surface: "O", color: "#448AFF" },
      ],
    },
    caries: [],
  };

  // Caries teeth
  [12, 29].forEach((n) => {
    map[n] = {
      toothNumber: n,
      baseStatus: "permanent",
      procedures: { completed: [], planned: [] },
      surfaces: { completed: [], planned: [] },
      caries: n === 12 ? ["O", "M"] : ["O"],
    };
  });

  return map;
}

/* ── Component ────────────────────────────────────────── */

const PMSChartingSlideAnimated = () => {
  const [phase, setPhase] = useState(0);
  const teeth = useMemo(() => generateMockTeeth(), []);
  const enhancedTeeth = useMemo(() => buildEnhancedTeeth(), []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase((p) => (p + 1) % NUM_PHASES);
    }, PHASE_DURATION);
    return () => clearInterval(interval);
  }, []);

  const current = phases[phase];
  const colors = colorClasses[current.color];

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-6 lg:p-10 relative overflow-hidden">
      {/* Background gradients */}
      <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
      <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-100 rounded-full blur-[100px] opacity-60" />

      <div className="flex flex-row h-full w-full max-w-[1500px] gap-8 relative z-10">
        {/* ── Left Side ───────────────────────── */}
        <div className="w-[30%] flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200 mb-4">
              <FileText className="w-4 h-4" />
              <span>PMS Feature</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
              Visual{" "}
              <span className="text-blue-600">Clinical Charting</span>
            </h2>

            <p className="text-base text-slate-600 leading-relaxed mb-6">
              Comprehensive digital charting with real-time condition tracking
              and integrated perio workflows.
            </p>
          </motion.div>

          {/* Phase indicators */}
          <div className="space-y-2">
            {phases.map((p, i) => {
              const active = i === phase;
              const Icon = p.icon;
              const pColors = colorClasses[p.color];
              return (
                <motion.button
                  key={p.key}
                  onClick={() => setPhase(i)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-300 text-left ${
                    active
                      ? `${pColors.bg} ${pColors.border} ${pColors.text} shadow-sm ring-2 ${pColors.ring}`
                      : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}
                >
                  <Icon
                    className={`w-4 h-4 shrink-0 ${
                      active ? pColors.text : "text-slate-400"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-sm font-bold ${
                        active ? pColors.text : "text-slate-700"
                      }`}
                    >
                      {p.label}
                    </div>
                    <AnimatePresence>
                      {active && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="text-xs mt-0.5 opacity-80"
                        >
                          {p.desc}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  {/* Progress bar for active phase */}
                  {active && (
                    <div className="w-8 h-1 bg-white/50 rounded-full overflow-hidden shrink-0">
                      <motion.div
                        className={`h-full rounded-full ${pColors.barBg}`}
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{
                          duration: PHASE_DURATION / 1000,
                          ease: "linear",
                        }}
                        key={`bar-${phase}`}
                      />
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* ── Right Side: Animated Tooth Chart ── */}
        <div className="w-[70%] h-full flex items-center justify-center">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full h-full bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
          >
            {/* Card header */}
            <div className="h-12 border-b border-slate-100 flex items-center justify-between px-5 bg-slate-50/50 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm leading-tight">
                    Sarah Johnson
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Patient #12345 &bull; Age 34
                  </p>
                </div>
              </div>
              <Badge className="bg-blue-600 text-white hover:bg-blue-700 border-0 text-[11px]">
                Active Chart
              </Badge>
            </div>

            {/* Phase status bar */}
            <div className="px-5 py-2 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.key}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.25 }}
                  className="flex items-center gap-2"
                >
                  <motion.div
                    animate={{ scale: [1, 1.4, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className={`w-2 h-2 rounded-full ${colors.dot}`}
                  />
                  <span className={`text-sm font-bold ${colors.text}`}>
                    {current.label}
                  </span>
                  <span className="text-xs text-slate-400">—</span>
                  <span className="text-xs text-slate-500">
                    {current.stat}
                  </span>
                </motion.div>
              </AnimatePresence>

              {/* Phase dots */}
              <div className="flex items-center gap-1.5">
                {phases.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPhase(i)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      i === phase
                        ? `${colors.dot} scale-125`
                        : "bg-slate-200 hover:bg-slate-300"
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Tooth chart container — takes remaining space */}
            <div className="flex-1 relative overflow-hidden min-h-0 flex items-center justify-center">
              <div
                className="w-full"
                style={{
                  transform: "scale(0.85)",
                  transformOrigin: "center center",
                }}
              >
                <InteractiveToothChart
                  teeth={teeth}
                  compact
                  hideDetailPanel
                  selectedTooth={current.selectedTooth}
                  onSelectTooth={() => {}}
                  enhancedTeeth={enhancedTeeth}
                  highlightedTeeth={current.highlightTeeth}
                  layerVisibility={{
                    completedProcedures: true,
                    plannedProcedures: true,
                    surfaceLabels: false,
                    caries: true,
                    bridges: true,
                    existingRestorations: true,
                    missingTeeth: true,
                    implants: true,
                    rootCanals: true,
                    crowns: true,
                    fractures: true,
                  }}
                />
              </div>
            </div>

            {/* Bottom legend bar */}
            <div className="px-5 py-2 border-t border-slate-100 bg-slate-50/30 flex-shrink-0">
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
                {[
                  { label: "Healthy", color: "#ede5d4", border: "#a89060" },
                  { label: "Completed", color: "#388E3C", border: "#388E3C" },
                  { label: "Planned", color: "#448AFF", border: "#448AFF" },
                  { label: "Caries", color: "transparent", border: "#000407" },
                  { label: "Missing", color: "#E9E9E9", border: "#9ca3af" },
                  { label: "Crown", color: "#dbeafe", border: "#2563eb" },
                  { label: "Implant", color: "#ecfeff", border: "#06b6d4" },
                  { label: "Root Canal", color: "#fef9c3", border: "#d97706" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-1.5 text-[10px] text-slate-500"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-sm border"
                      style={{
                        background: item.color,
                        borderColor: item.border,
                      }}
                    />
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PMSChartingSlideAnimated;
